// Product imagery for the /marketplace sealed & merchandise board.
//
// Everything here is real product photography or official key-visual art:
//   • Pocketpair/Bushiroad official product art and product photos (booster
//     pack wrapper, Trial Deck boxes, booster box, rubber playmat, storage
//     box, sleeves, "Legends Awaken" announcement key visual).
//   • Retail product photos of official licensed Palworld merchandise
//     (Chillet plush, Cattiva figure, Cattiva keychain, Palworld hoodie).
// `official: false` is only used where the art stands in for a product whose
// own photography has not been published yet (BP02 Legends Awaken sealed
// product), in which case the official announcement visual is shown.
import packFront from "@/assets/pack-front.png.asset.json";
import deckRedBlue from "@/assets/deck-red-blue.png.asset.json";
import deckGreenPurple from "@/assets/deck-green-purple.png.asset.json";
import td03EternalAscent from "@/assets/market/td03-eternal-ascent.png.asset.json";
import td04EternalAscent from "@/assets/market/td04-eternal-ascent.png.asset.json";
import boosterBox from "@/assets/market/bp01-box-dark.jpg.asset.json";
import legendsAwaken from "@/assets/market/bp02-kv.webp.asset.json";
import playmat from "@/assets/market/playmat.webp.asset.json";
import storageBox from "@/assets/market/storage-box.webp.asset.json";
import sleeves from "@/assets/market/sleeves.webp.asset.json";
import ss01KeyVisual from "@/assets/ss01/slide.webp.asset.json";
import plush from "@/assets/market/plush-chillet.webp.asset.json";
import figure from "@/assets/market/figure-collector-set.jpg.asset.json";
import keychain from "@/assets/market/keychain-cattiva.webp.asset.json";
import apparel from "@/assets/market/apparel-hoodie.webp.asset.json";
import sealedGeneric from "@/assets/market/sealed-generic.jpg";

export interface SealedImage {
  src: string;
  alt: string;
  official: boolean;
}

export const SEALED_IMAGES: Record<string, SealedImage> = {
  "bp01-pack": { src: packFront.url, alt: "Palworld OFFICIAL CARD GAME Dawn of Palpagos booster pack", official: true },
  "bp02-pack": {
    src: legendsAwaken.url,
    alt: "Official Palworld OFFICIAL CARD GAME Legends Awaken booster pack announcement visual",
    official: false,
  },
  "bp01-box": {
    src: boosterBox.url,
    alt: "Palworld OFFICIAL CARD GAME Dawn of Palpagos booster box holding 12 sealed booster packs",
    official: true,
  },
  "bp02-box": {
    src: legendsAwaken.url,
    alt: "Official Palworld Legends Awaken key visual used until booster box photography is published",
    official: false,
  },
  "td01-deck": { src: deckRedBlue.url, alt: "Palworld OFFICIAL CARD GAME Trial Deck Red・Blue box", official: true },
  "td02-deck": { src: deckGreenPurple.url, alt: "Palworld OFFICIAL CARD GAME Trial Deck Green・Purple box", official: true },
  "td03-deck": {
    src: td03EternalAscent.url,
    alt: "Palworld OFFICIAL CARD GAME Eternal Ascent Trial Deck Red・Green box",
    official: true,
  },
  "td04-deck": {
    src: td04EternalAscent.url,
    alt: "Palworld OFFICIAL CARD GAME Eternal Ascent Trial Deck Blue・Purple box",
    official: true,
  },
  "bp02-case": {
    src: legendsAwaken.url,
    alt: "Official Palworld Legends Awaken key visual used until sealed-case photography is published",
    official: false,
  },
  "merch-sleeve-set": {
    src: ss01KeyVisual.url,
    alt: "Palworld OFFICIAL CARD GAME Sleeve & Card Set Vol. 1 product visual",
    official: true,
  },
  "merch-legends-awaken": {
    src: legendsAwaken.url,
    alt: "Official Palworld Legends Awaken key visual used for its accessory merchandise line",
    official: false,
  },
  case: {
    src: boosterBox.url,
    alt: "Sealed case of Palworld Dawn of Palpagos booster boxes",
    official: true,
  },
  "merch-plush": { src: plush.url, alt: "Official Palworld Chillet 30cm plush toy, eBay marketplace listing photo", official: true },
  "merch-figure": {
    src: figure.url,
    alt: "Official Palworld collector figure set with Chillet, Grizzbolt and Relaxaurus",
    official: true,
  },
  "merch-playmat": {
    src: playmat.url,
    alt: "Palworld OFFICIAL CARD GAME Petallia — Sweet Blessings rubber playmat",
    official: true,
  },
  "merch-sleeves": {
    src: sleeves.url,
    alt: "Palworld OFFICIAL CARD GAME Grizzbolt — Rumbling Tank card sleeves",
    official: true,
  },
  "merch-binder": {
    src: storageBox.url,
    alt: "Palworld OFFICIAL CARD GAME storage box for card and deck storage",
    official: true,
  },
  "merch-apparel": { src: apparel.url, alt: "Official Palworld hoodie, front and back print", official: true },
  "merch-keychain": { src: keychain.url, alt: "Official Palworld Cattiva keychain with strap", official: true },
};

export function sealedImage(key: string): SealedImage {
  return SEALED_IMAGES[key] ?? { src: sealedGeneric, alt: "Sealed trading card product", official: false };
}
